// ---------------------------------------------------------------- CONTACT PAGE (LG2)
// Reason + subject + message, with the member's own address prefilled from their account.
// Every message is stored server-side, so it is received whether or not mail is wired.
//
// >>> **MOVED VERBATIM OUT OF `src/App.tsx` (2026-09-03), AND THE EXTRACTION IS EARNED RATHER THAN
// ARGUED FOR** — roadmap 7's own method: *opportunistically, as areas are touched.* <<<
//
// **THIS IS THE DOOR EVERY MEMBER THAT ACK-NOACCOUNT-1 DROPPED CAME THROUGH.** That defect was the
// triage pass withholding the one sentence this house sends unprompted from anybody whose contact row
// carried no `user_id` — and `user_id` is filled from the SESSION, so it means *she wrote in signed
// out*, which is this form with nobody logged into it. c121's subject was "ACCESS ISSUES" and her
// message was *"I cannot log in"*: **the members it silenced were the ones this form exists for.**
// While it lived inside an eleven-thousand-line file, **no fixture could reach the surface that
// produces that row at all**, and the reach is what the move buys (R8's ceiling, R217's ruling).
//
// It holds hooks, so it is the `MembershipPage` / `TierCards` class rather than a hookless leaf a gate
// CALLS. What a gate gets is the IMPORT, and that is enough to hold the thing below.
import { useEffect, useState, type FormEvent } from "react";
import { ArrowLeft } from "lucide-react";

// >>> **WHERE THE ANSWER GOES, SAID TO BOTH KINDS OF VISITOR (ACK-NOACCOUNT-1, 2026-09-03).** <<<
//
// The signed-in hint has always been here. **A signed-out visitor was told nothing at all** — a bare
// address field, no sentence about what it is for — and until this evening that silence was accurate,
// because her message really did go nowhere a reply could reach. **It is not accurate any more:** the
// triage pass hands her own address to `sendSupportReply`, which answers a writer with no account by
// email. So the form says so.
//
// **TWO SENTENCES, NOT ONE WIDER ONE (R18).** *This is the address on your account* and *this is the
// address we will write back to* are different facts to the two different people reading them: one is
// being told her address is already known and fixed, the other is being told that what she types is
// the only way back to her. Collapsing them would lose the half that matters to whoever is typing.
export const CONTACT_ANSWER_HINT_MEMBER =
  "This is the address on your account, and where we will answer.";
export const CONTACT_ANSWER_HINT_VISITOR =
  "We will write back to this address, so please check it is one you read.";

export function ContactPage({ goHome }: { goHome: () => void }) {
  const [reasons, setReasons] = useState<string[]>([]);
  const [reason, setReason] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [prefilled, setPrefilled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");
  const [sent, setSent] = useState("");

  useEffect(() => {
    fetch("/api/contact/config")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) return;
        if (Array.isArray(d.reasons)) { setReasons(d.reasons); setReason((r) => r || d.reasons[0] || ""); }
        // Signed in: the address comes from the account and is shown, not asked for.
        if (d.email) { setEmail(d.email); setPrefilled(true); }
      })
      .catch(() => {});
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy) return;
    setBusy(true);
    setStatus("");
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ reason, subject: subject.trim(), message: message.trim(), email: email.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false) { setStatus(data?.message || "Your message could not be sent right now."); return; }
      setSent(data.message || "Your message is with us.");
    } catch {
      setStatus("Your message could not be sent right now. Please try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page-shell contact-page">
      <button className="back-button" type="button" onClick={goHome}>
        <ArrowLeft size={18} />
        Back
      </button>
      <div className="contact-page__panel">
        <p className="eyebrow">Contact</p>
        <h1>Talk to us</h1>
        {sent ? (
          <div className="contact-page__done">
            <p className="contact-page__done-title">Thank you. {sent}</p>
            <button type="button" className="button button--ghost" onClick={() => { setSent(""); setSubject(""); setMessage(""); }}>
              Send another message
            </button>
          </div>
        ) : (
          <form className="contact-page__form" onSubmit={submit}>
            <label className="contact-page__field">
              <span>What is this about?</span>
              <select value={reason} onChange={(e) => setReason(e.target.value)}>
                {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="contact-page__field">
              <span>Your email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                readOnly={prefilled}
                placeholder="you@example.com"
              />
              {/* Both visitors are told where the answer goes; only the sentence differs. */}
              <small className="contact-page__hint">
                {prefilled ? CONTACT_ANSWER_HINT_MEMBER : CONTACT_ANSWER_HINT_VISITOR}
              </small>
            </label>
            <label className="contact-page__field">
              <span>Subject</span>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="A few words on what you need" />
            </label>
            <label className="contact-page__field">
              <span>Message</span>
              <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={8} placeholder="Tell us what is going on and we will help." />
            </label>
            <button className={busy ? "button button--primary is-generating" : "button button--primary"} type="submit" disabled={busy}>
              {busy ? "Sending..." : "Send message"}
            </button>
            {status ? <p className="contact-page__status">{status}</p> : null}
          </form>
        )}
      </div>
    </section>
  );
}
